/**
 * DJI Products Banner JavaScript
 * 处理产品展示区域的交互功能
 */

class DJIProductsBanner {
  constructor() {
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
    this.setupVideoHandling();
    this.setupKeyboardNavigation();
    this.setupLazyLoading();
  }

  /**
   * 设置交叉观察器用于动画
   */
  setupIntersectionObserver() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('aos-animate');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '50px'
      });

      // 观察所有带有 data-aos 属性的元素
      document.querySelectorAll('[data-aos]').forEach(el => {
        observer.observe(el);
      });
    } else {
      // 如果不支持 IntersectionObserver，直接显示所有元素
      document.querySelectorAll('[data-aos]').forEach(el => {
        el.classList.add('aos-animate');
      });
    }
  }

  /**
   * 设置视频处理
   */
  setupVideoHandling() {
    // 处理视频播放按钮
    document.querySelectorAll('.video-play-button').forEach(button => {
      button.addEventListener('click', this.handleVideoPlay.bind(this));
    });

    // 处理自动播放视频的性能优化
    this.setupVideoOptimization();
  }

  /**
   * 处理视频播放
   */
  handleVideoPlay(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.currentTarget;
    const videoEmbed = button.closest('.dji-product-video-embed');
    const videoUrl = videoEmbed.dataset.videoUrl;
    
    if (videoUrl) {
      // 创建视频模态框或跳转到视频页面
      this.openVideoModal(videoUrl);
    }
  }

  /**
   * 打开视频模态框
   */
  openVideoModal(videoUrl) {
    // 创建模态框
    const modal = document.createElement('div');
    modal.className = 'dji-video-modal';
    modal.innerHTML = `
      <div class="dji-video-modal-overlay">
        <div class="dji-video-modal-content">
          <button class="dji-video-modal-close" aria-label="关闭视频">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="dji-video-container">
            <iframe 
              src="${this.getEmbedUrl(videoUrl)}" 
              frameborder="0" 
              allowfullscreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture">
            </iframe>
          </div>
        </div>
      </div>
    `;

    // 添加样式
    const style = document.createElement('style');
    style.textContent = `
      .dji-video-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .dji-video-modal-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
      }
      .dji-video-modal-content {
        position: relative;
        width: 100%;
        max-width: 1200px;
        aspect-ratio: 16/9;
      }
      .dji-video-modal-close {
        position: absolute;
        top: -3rem;
        right: 0;
        background: none;
        border: none;
        color: white;
        font-size: 1.5rem;
        cursor: pointer;
        padding: 0.5rem;
        z-index: 1;
      }
      .dji-video-container {
        width: 100%;
        height: 100%;
      }
      .dji-video-container iframe {
        width: 100%;
        height: 100%;
        border-radius: 8px;
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(modal);

    // 添加关闭事件
    const closeBtn = modal.querySelector('.dji-video-modal-close');
    const overlay = modal.querySelector('.dji-video-modal-overlay');
    
    const closeModal = () => {
      document.body.removeChild(modal);
      document.head.removeChild(style);
    };

    closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal();
    });

    // ESC键关闭
    document.addEventListener('keydown', function escHandler(e) {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', escHandler);
      }
    });
  }

  /**
   * 获取嵌入式视频URL
   */
  getEmbedUrl(url) {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = this.extractYouTubeId(url);
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    } else if (url.includes('vimeo.com')) {
      const videoId = this.extractVimeoId(url);
      return `https://player.vimeo.com/video/${videoId}?autoplay=1`;
    }
    return url;
  }

  /**
   * 提取YouTube视频ID
   */
  extractYouTubeId(url) {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  }

  /**
   * 提取Vimeo视频ID
   */
  extractVimeoId(url) {
    const regExp = /(?:vimeo)\.com.*(?:videos|video|channels|)\/([\d]+)/i;
    const match = url.match(regExp);
    return match ? match[1] : null;
  }

  /**
   * 设置视频优化
   */
  setupVideoOptimization() {
    // 检测是否为移动设备或低性能设备
    const isLowPerformance = this.isLowPerformanceDevice();
    
    if (isLowPerformance) {
      // 在低性能设备上禁用自动播放视频
      document.querySelectorAll('.dji-product-video-element').forEach(video => {
        video.removeAttribute('autoplay');
        video.setAttribute('preload', 'none');
      });
    }
  }

  /**
   * 检测是否为低性能设备
   */
  isLowPerformanceDevice() {
    // 检测连接类型
    if ('connection' in navigator) {
      const connection = navigator.connection;
      if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
        return true;
      }
    }

    // 检测设备内存
    if ('deviceMemory' in navigator && navigator.deviceMemory < 4) {
      return true;
    }

    // 检测CPU核心数
    if ('hardwareConcurrency' in navigator && navigator.hardwareConcurrency < 4) {
      return true;
    }

    return false;
  }

  /**
   * 设置键盘导航
   */
  setupKeyboardNavigation() {
    document.querySelectorAll('.dji-product-item').forEach(item => {
      // 使产品项可聚焦
      if (!item.hasAttribute('tabindex')) {
        item.setAttribute('tabindex', '0');
      }

      // 添加键盘事件
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const link = item.querySelector('.dji-product-link');
          if (link) {
            link.click();
          }
        }
      });
    });
  }

  /**
   * 设置懒加载
   */
  setupLazyLoading() {
    if ('loading' in HTMLImageElement.prototype) {
      // 浏览器原生支持懒加载
      document.querySelectorAll('img[loading="lazy"]').forEach(img => {
        img.addEventListener('load', () => {
          img.classList.add('loaded');
        });
      });
    } else {
      // 使用 Intersection Observer 实现懒加载
      this.implementLazyLoading();
    }
  }

  /**
   * 实现懒加载
   */
  implementLazyLoading() {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.classList.add('loaded');
          imageObserver.unobserve(img);
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
}

// 初始化
document.addEventListener('DOMContentLoaded', () => {
  new DJIProductsBanner();
});

// 处理动态添加的内容
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new DJIProductsBanner();
  });
} 